import React, { FC, ReactElement, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { FormControl } from '../../ui/FormControl';
import { ReactComponent as Close } from '../../../assets/images/close.svg';
import { ReactComponent as FillClose } from '../../../assets/images/fillClose.svg';
import classes from './ChangeUserPassword.module.scss';
import { ReactComponent as Point } from '../../../assets/images/point.svg';
import { useAnalytics } from '../../../hooks/entityHooks';
import { User } from '../../../types/user';

type FormikValues = {
  oldPassword: string;
  newPassword: string;
  approvePassword: string;
};

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  userInfo: User;
  isFullModal: boolean;
};

export const ChangeUserPassword = ({
  open,
  setOpen,
  userInfo,
  isFullModal,
}: Props): ReactElement => {
  const handleClose = () => {
    setOpen(false);
  };
  const { changePasswordAdmin, changePasswordUser } = useAnalytics();
  const useStyles = makeStyles({
    root: {
      background: '#FF7221',
      borderRadius: 0,
      color: 'white',
      width: 190,
      fontSize: 16,
      fontFamily: 'HelveticaNeueCyr',
      fontWeight: 550,
      textTransform: 'none',
      '&:hover, &:focus': {
        background: '#C8524D',
      },
      height: 50,
      padding: '0 5px',
      marginLeft: 10,
    },
    cancel: {
      borderRadius: 0,
      color: '#FF7221',
      fontWeight: 550,
      width: 190,
      fontSize: 16,
      border: ['1px', 'solid', '#FF7221'].join(' '),
      borderColor: '#FF7221',
      fontFamily: 'HelveticaNeueCyr',
      // '&:hover, &:focus': {
      //   background: '#EB564F',
      // },
      // boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
      height: 50,
      padding: '0 5px',
      margitRight: 10,
      textTransform: 'none',
    },
  });

  const {
    handleSubmit,
    handleBlur,
    values,
    errors,
    touched,
    setFieldValue,
  } = useFormik<FormikValues>({
    initialValues: {
      oldPassword: isFullModal ? '' : 'value',
      newPassword: '',
      approvePassword: '',
    },
    onSubmit: () => {
      if (isFullModal) {
        changePasswordUser(values.oldPassword, values.newPassword).then(() =>
          setOpen(!open),
        );
      } else {
        changePasswordAdmin(values.newPassword, userInfo.id).then(() =>
          setOpen(!open),
        );
      }
    },
    validationSchema: Yup.object().shape({
      oldPassword: Yup.string().required('Обязательно для заполнения'),
      newPassword: Yup.string()
        .min(8, 'Пароль должен быть длиннее 8 символов')
        .notOneOf(
          [Yup.ref('oldPassword'), ''],
          'Новый пароль совпадает со старым',
        )
        .required('Обязательно для заполнения'),
      approvePassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), ''], 'Пароли не совпадают')
        .notOneOf(
          [Yup.ref('oldPassword'), ''],
          'Новый пароль совпадает со старым',
        )
        .required('Обязательно для заполнения'),
    }),
  });
  const [focus, setFocus] = useState(false);

  const style = useStyles();
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <div className={classes.form__dialogTitle}>
            <div className={classes.form__dialogTitle__title}>
              Изменить пароль
            </div>
            <div
              className={classes.close}
              onMouseOver={(e) => setFocus(true)}
              onMouseOut={(e) => setFocus(false)}
              onClick={(e) => setOpen(false)}
            >
              {focus ? <FillClose /> : <Close />}
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {isFullModal && (
            <div className={classes.dialogContent_item}>
              <div className={classes.title}>
                <p>Старый пароль</p>
                <Point className={classes.title__point} />
              </div>

              <div>
                <FormControl
                  placeholder="Введите пароль"
                  type="password"
                  name="oldPassword"
                  value={values.oldPassword}
                  onChange={(e) => setFieldValue('oldPassword', e.target.value)}
                  error={Boolean(errors?.oldPassword && touched?.oldPassword)}
                  errorMessage={errors?.oldPassword}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          )}

          <div className={classes.dialogContent_item}>
            <div className={classes.title}>
              <p>Новый пароль</p>
              <Point className={classes.title__point} />
            </div>

            <div>
              <FormControl
                placeholder="Придумайте пароль"
                type="password"
                name="newPassword"
                value={values.newPassword}
                onChange={(e) => setFieldValue('newPassword', e.target.value)}
                error={Boolean(errors?.newPassword && touched?.newPassword)}
                errorMessage={errors?.newPassword}
                onBlur={handleBlur}
              />
            </div>
          </div>
          <div className={classes.dialogContent_item}>
            <div className={classes.title}>
              <p>Подтвердите новый пароль</p>
              <Point className={classes.title__point} />
            </div>

            <div>
              <FormControl
                placeholder="Повторите новый пароль"
                type="password"
                name="oldPassword"
                value={values.approvePassword}
                onChange={(e) =>
                  setFieldValue('approvePassword', e.target.value)
                }
                error={Boolean(
                  errors?.approvePassword && touched?.approvePassword,
                )}
                errorMessage={errors?.approvePassword}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </DialogContent>
        <div className={classes.form__row__button}>
          <Button
            className={style.cancel}
            onClick={handleClose}
            color="primary"
          >
            Отмена
          </Button>
          <Button
            className={style.root}
            // onClick={handleClose}
            type="submit"
            color="primary"
          >
            Далее
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
